export const SUPPORT_CENTER_WEEKDAYS = [
  { label: 'Dom', value: 0 },
  { label: 'Seg', value: 1 },
  { label: 'Ter', value: 2 },
  { label: 'Qua', value: 3 },
  { label: 'Qui', value: 4 },
  { label: 'Sex', value: 5 },
  { label: 'Sáb', value: 6 },
] as const

export const SUPPORT_CENTER_MONTHS = [
  { label: 'Janeiro', value: 1 },
  { label: 'Fevereiro', value: 2 },
  { label: 'Março', value: 3 },
  { label: 'Abril', value: 4 },
  { label: 'Maio', value: 5 },
  { label: 'Junho', value: 6 },
  { label: 'Julho', value: 7 },
  { label: 'Agosto', value: 8 },
  { label: 'Setembro', value: 9 },
  { label: 'Outubro', value: 10 },
  { label: 'Novembro', value: 11 },
  { label: 'Dezembro', value: 12 },
] as const

export function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate()
}

export function buildIsoDate(year: number, month: number, day: number): string {
  const y = String(year).padStart(4, '0')
  const m = String(month).padStart(2, '0')
  const d = String(day).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export function parseIsoDate(iso: string): { year: number; month: number; day: number } {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number)
  return { year: y, month: m, day: d }
}

export function formatWeekdaysSummary(weekdays: number[]): string {
  if (!weekdays.length) return '—'
  const dayLabels = SUPPORT_CENTER_WEEKDAYS.filter((d) => weekdays.includes(d.value)).map(
    (d) => d.label
  )
  return dayLabels.length ? dayLabels.join(', ') : '—'
}

function formatHourLabel(hour: number): string {
  return `${hour.toString().padStart(2, '0')}h`
}

/** Agrupa horas seguidas: [8, 9, 10, 14] → "08h–10h, 14h". */
export function formatHourRanges(hours: number[], separator = ', '): string {
  const sorted = sanitizeHoursForDay(hours)
  if (!sorted.length) return '—'

  const ranges: string[] = []
  let start = sorted[0]
  let end = sorted[0]

  const push = (from: number, to: number) => {
    ranges.push(from === to ? formatHourLabel(from) : `${formatHourLabel(from)}–${formatHourLabel(to)}`)
  }

  for (let i = 1; i < sorted.length; i++) {
    const hour = sorted[i]
    if (hour === end + 1) {
      end = hour
      continue
    }
    push(start, end)
    start = hour
    end = hour
  }
  push(start, end)
  return ranges.join(separator)
}

export function formatHoursSummary(hours: number[]): string {
  return formatHourRanges(hours)
}

export type WeekdayHoursEntry = {
  day: number
  label: string
  hours: number[]
}

function hoursListsEqual(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

/** Dias com horário, na ordem da semana. Sem mapa, usa o cruzamento legado dias × horas. */
export function listHoursByWeekday(
  hoursByWeekday: Record<number, number[]>,
  weekdaysFallback: number[] = [],
  hoursFallback: number[] = []
): WeekdayHoursEntry[] {
  const entries = SUPPORT_CENTER_WEEKDAYS.map((d) => {
    const hours = sanitizeHoursForDay(hoursByWeekday[d.value])
    if (hours.length === 0) return null
    const entry: WeekdayHoursEntry = { day: d.value, label: d.label, hours }
    return entry
  }).filter((e): e is WeekdayHoursEntry => e != null)

  if (entries.length > 0) return entries

  const shared = sanitizeHoursForDay(hoursFallback)
  if (weekdaysFallback.length === 0 || shared.length === 0) return []

  return SUPPORT_CENTER_WEEKDAYS.filter((d) => weekdaysFallback.includes(d.value)).map((d) => ({
    day: d.value,
    label: d.label,
    hours: shared,
  }))
}

export function weekdaysShareHours(entries: WeekdayHoursEntry[]): boolean {
  if (entries.length <= 1) return true
  const first = entries[0].hours
  return entries.every((entry) => hoursListsEqual(entry.hours, first))
}

export type WeekdayHoursGroup = {
  key: string
  dayLabel: string
  hours: number[]
}

/** Dias seguidos no mesmo grupo: Seg–Sex. Dias soltos: Ter, Qui. */
export function formatWeekdaySpan(entries: { day: number; label: string }[]): string {
  if (entries.length === 0) return '—'
  const sorted = [...entries].sort((a, b) => a.day - b.day)

  const runs: { start: string; end: string; count: number }[] = []
  let start = sorted[0]
  let prev = sorted[0]
  let count = 1

  const push = () => {
    runs.push({ start: start.label, end: prev.label, count })
  }

  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i]
    if (current.day === prev.day + 1) {
      prev = current
      count += 1
      continue
    }
    push()
    start = current
    prev = current
    count = 1
  }
  push()

  return runs
    .map((run) => (run.count === 1 ? run.start : `${run.start}–${run.end}`))
    .join(', ')
}

/** Junta dias com o mesmo horário, na ordem da semana. */
export function groupWeekdayHours(entries: WeekdayHoursEntry[]): WeekdayHoursGroup[] {
  const order: string[] = []
  const bySignature = new Map<string, WeekdayHoursEntry[]>()

  for (const entry of entries) {
    const signature = entry.hours.join(',')
    const bucket = bySignature.get(signature)
    if (!bucket) {
      bySignature.set(signature, [entry])
      order.push(signature)
      continue
    }
    bucket.push(entry)
  }

  return order.map((signature) => {
    const days = bySignature.get(signature) ?? []
    return {
      key: `${days.map((day) => day.day).join('-')}:${signature}`,
      dayLabel: formatWeekdaySpan(days),
      hours: days[0]?.hours ?? [],
    }
  })
}

/**
 * Resumo textual: se todos os dias tiverem o mesmo horário, lista só as horas;
 * se divergirem, "Ter 08h–10h · Qui 14h".
 */
export function formatHoursByWeekdaySummary(
  hoursByWeekday: Record<number, number[]>,
  weekdaysFallback: number[] = [],
  hoursFallback: number[] = []
): string {
  const entries = listHoursByWeekday(hoursByWeekday, weekdaysFallback, hoursFallback)
  if (entries.length === 0) return '—'
  if (weekdaysShareHours(entries)) return formatHourRanges(entries[0].hours)
  return entries.map((e) => `${e.label} ${formatHourRanges(e.hours)}`).join(' · ')
}

function sanitizeHoursForDay(hours: number[] | undefined): number[] {
  if (!hours?.length) return []
  return [...hours]
    .filter((h) => Number.isInteger(h) && h >= 0 && h <= 23)
    .sort((a, b) => a - b)
}

export function formatDateBr(isoDate: string): string {
  const { year, month, day } = parseIsoDate(isoDate)
  return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`
}

export function toggleSortedNumber(list: number[], value: number): number[] {
  const set = new Set(list)
  if (set.has(value)) set.delete(value)
  else set.add(value)
  return Array.from(set).sort((a, b) => a - b)
}
